import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTable, faLayerGroup, faBolt, faSliders, faCogs } from '@fortawesome/free-solid-svg-icons';
import { version } from '../../data/constant';
import { subscriberReply, subscriberRequest, addressRequest, addressReply, bandwidthReply, bandwidthRequest, techReply, techRequest } from './example';
import XmlBeautify from 'xml-beautify';
import '../../asset/css/portal.css';

const monitorTools = [
  { title: 'History', path: '/monitor/history', icon: faSearch, description: 'Trace service calls, narrow down requests, and inspect request and response details.' },
  { title: 'Access Inventory', path: '/monitor/ai', icon: faTable, description: 'Look up address infrastructure, installations, and network-provider identifiers.' },
  { title: 'Catalog', path: '/monitor/catalog', icon: faLayerGroup, description: 'Explore the master data and mappings between resources and services.' },
  { title: 'Service Catalog', path: '/monitor/serviceCatalog', icon: faLayerGroup, description: 'Review service definitions and technology-specific configuration.' },
  { title: 'Max speed', path: '/monitor/speed', icon: faBolt, description: 'Inspect the maximum-speed configuration for supported network providers.' },
  { title: 'Swagger', path: '/visitation-swagger', icon: faCogs, description: 'Browse the Visitation API documentation and available operations.' }
];

const historyFields = [
  ['Request dates', 'Limit the time window using Request from date and Request to date.'],
  ['Request / response', 'Search for text contained in a service request or response.'],
  ['Primary identifier / service type', 'Narrow the search to a specific identifier or service.'],
  ['Duration / vendor', 'Filter by call duration in milliseconds or network provider.'],
  ['Order number / request ID', 'Find calls associated with an order or a history record.'],
  ['Address ID / records', 'Use the address identifier and choose how many rows to retrieve.'],
  ['Search from archive', 'Use the archive option when searching archived history.']
];

const inventoryFields = [
  ['MAD ID', 'The MAD address identifier used to find infrastructure at a specific address. Use Get MadId after filling the address fields, or enter it manually.'],
  ['Installation ID', 'The internal Visitation installation identifier, used as part of the KAP ID on an order.'],
  ['Vendor', 'The network provider selected from the current vendor list.'],
  ['Vendor Address ID', 'The address identifier used by the network provider.'],
  ['External Installation ID', 'The installation identifier used by the network provider.']
];

const serviceExamples = [
  { title: 'Pre-qualification', service: 'Visitation Subscriber', description: 'Identify the available installation lines at an address. The response includes network-capacity information from the provider data load.', request: subscriberRequest, response: subscriberReply },
  { title: 'Product offer', service: 'VisitationByAddress', description: 'Retrieve Customer Facing Services and the maximum line speed for the selected installation line.', request: addressRequest, response: addressReply },
  { title: 'Bandwidth calculation', service: 'CalcBandwidthByAddress', description: 'Validate the selected products and available bandwidth. Where applicable, a real-time provider check supplements the loaded infrastructure data.', request: bandwidthRequest, response: bandwidthReply },
  { title: 'Provisioning', service: 'TechParam', description: 'Retrieve provisioning information for SOM, ADMAN, and the network provider, including service identifiers, speeds, QoS, and channel information.', request: techRequest, response: techReply }
];

function FieldGuide({ fields, caption }) {
  return (
    <div className="portal-table-scroll">
      <table className="table table-sm">
        <caption>{caption}</caption>
        <thead><tr><th scope="col">Filter</th><th scope="col">How to use it</th></tr></thead>
        <tbody>{fields.map(([name, description]) => (
          <tr key={name}><th scope="row">{name}</th><td>{description}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function PortalInfo() {
  const xmlFormatter = new XmlBeautify();

  return (
    <div className="portal">
      <header className="portal-hero">
        <p className="portal-eyebrow">TDC Brands / Visitation</p>
        <h1>Your guide to Visitation Dashboard</h1>
        <p>Find address infrastructure, investigate service calls, and understand the data behind order qualification and fulfillment.</p>
        <span className="portal-version">{version}</span>
      </header>

      <nav className="portal-jump-links" aria-label="Portal guide sections">
        <a href="#portal-tools">Explore the tools</a>
        <a href="#portal-search">Search guide</a>
        <a href="#portal-services">Service flow</a>
      </nav>

      <section id="portal-tools" className="portal-section" aria-labelledby="portal-tools-title">
        <h2 id="portal-tools-title">Start with the task at hand</h2>
        <p>Use the sidebar for navigation or open one of the tools below. Available actions depend on your account permissions.</p>
        <div className="portal-tool-grid">
          {monitorTools.map(tool => (
            <Link to={tool.path} className="portal-tool" key={tool.path}>
              <FontAwesomeIcon icon={tool.icon} aria-hidden="true"/>
              <h3>{tool.title}</h3>
              <p>{tool.description}</p>
              <span className="portal-tool-action">Open {tool.title} &rarr;</span>
            </Link>
          ))}
        </div>
        <aside className="portal-note">
          <h3><FontAwesomeIcon icon={faSliders} aria-hidden="true"/> Role-based tools</h3>
          <p>Management appears for accounts with the OPERATOR role and includes Property, Env, and Cache. Transaction tools appear for INTERNAL accounts and include Show Details, Delta Info, and Retry. Additional inventory views and service execution are available when enabled for the environment.</p>
          <p>Use the controls available to your account; changes to configuration or transaction retries can affect live operations.</p>
        </aside>
      </section>

      <section id="portal-search" className="portal-section" aria-labelledby="portal-search-title">
        <h2 id="portal-search-title">Search with confidence</h2>
        <div className="portal-guide-grid">
          <article>
            <h3>Investigate a service call</h3>
            <ol>
              <li>Open History and set the request date range.</li>
              <li>Add an identifier, service type, vendor, or request/response text to narrow the results.</li>
              <li>Select Search, then use the result&apos;s view action to inspect the request and response. Use Reset to clear the filters.</li>
            </ol>
            <FieldGuide fields={historyFields} caption="History search filters"/>
          </article>
          <article>
            <h3>Find address infrastructure</h3>
            <ol>
              <li>Open Access Inventory and enter a MAD ID or installation identifier. Alternatively, type at least four characters in Search address (NAM) and select a complete address to fill the address fields below.</li>
              <li>If you only have a postal address, use the address lookup: enter the street, house number, and postcode, with house letter, floor, or side when needed.</li>
              <li>Select Get MadId to populate the MAD ID, then Search to retrieve the inventory.</li>
            </ol>
            <FieldGuide fields={inventoryFields} caption="Access Inventory search filters"/>
          </article>
        </div>
        <aside className="portal-note">
          <h3>Working with address data</h3>
          <p>The Find / Hide control toggles the address lookup fields. Provider files supply the underlying infrastructure data, including installation changes and availability updates. Inventory data and a real-time provider response may represent different points in time.</p>
        </aside>
      </section>

      <section id="portal-services" className="portal-section" aria-labelledby="portal-services-title">
        <h2 id="portal-services-title">From qualification to fulfillment</h2>
        <p>The service flow starts with available installations, moves through product and bandwidth validation, and ends with provisioning data. Expand a step to review the reference XML examples.</p>
        <div className="portal-service-flow">
          {serviceExamples.map((example, index) => (
            <details className="portal-service-step" key={example.service}>
              <summary>{index + 1}. {example.title} <span>{example.service}</span></summary>
              <div className="portal-service-content">
                <p>{example.description}</p>
                <p className="portal-example-label">Illustrative examples, not live data. Actual payloads depend on the service and environment.</p>
                <div className="portal-example-grid">
                  <div><h3>Request</h3><pre tabIndex="0" aria-label={`${example.service} request example`}>{xmlFormatter.beautify(example.request, { indent: '  ', useSelfClosingElement: true })}</pre></div>
                  <div><h3>Response</h3><pre tabIndex="0" aria-label={`${example.service} response example`}>{xmlFormatter.beautify(example.response, { indent: '  ', useSelfClosingElement: true })}</pre></div>
                </div>
              </div>
            </details>
          ))}
        </div>
        <aside className="portal-note">
          <h3>Real-time validation</h3>
          <p>Provider checks can return earliest delivery date, technician requirements, and available capacity. A response containing <code>VSCACHE_NUUDAY_REAL_TIME</code> identifies real-time information; <code>VSCACHE_NUUDAY</code> identifies loaded inventory data. When a provider check times out, loaded whitelist data may be used.</p>
          <p>Real-time checks depend on the order action and request settings, including <code>PreSaleCapacityCheck</code>. A bandwidth request with <code>CpeFeatureAction</code> set to <code>MUST</code> performs the final validation before fulfillment. Consult Swagger for the current API contract.</p>
        </aside>
      </section>
    </div>
  );
}
